import type { TaskUtils } from './types';
import { caughtErrorDetail, warnSdkUtilFallback } from './warn';

const numberFormatters = new Map<string, Intl.NumberFormat>();

function numberFormatter(locale: string, decimals: number): Intl.NumberFormat {
  const key = `${locale}|${decimals}`;

  let formatter = numberFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    numberFormatters.set(key, formatter);
  }

  return formatter;
}

export function formatNumber(value: unknown, decimals: number = 2): string | null {
  if (value === null || value === undefined) {
    warnSdkUtilFallback('number.format', 'No number was provided; returning null.');
    return null;
  }

  try {
    if (
      (typeof value !== 'number' && typeof value !== 'string') ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      warnSdkUtilFallback(
        'number.format',
        'The value is not a finite number or non-empty numeric string; returning null.'
      );
      return null;
    }

    const numValue = typeof value === 'number' ? value : Number(value);
    const safeDecimals = Math.max(0, Number(decimals ?? 2));

    if (!Number.isFinite(numValue)) {
      warnSdkUtilFallback('number.format', 'The value is not a finite number; returning null.');
      return null;
    }

    return numberFormatter(navigator.language, safeDecimals).format(numValue);
  } catch (error) {
    warnSdkUtilFallback(
      'number.format',
      `The number could not be formatted: ${caughtErrorDetail(error)}. Returning null.`
    );
    return null;
  }
}

export const numberUtils: TaskUtils['number'] = {
  format: (value, options) => formatNumber(value, options?.decimals ?? 2)
};
